<template>
  <div class="w-full">
    <h2
      class="w-full mb-4 mr-4"
      :class="isContributor ? 'font-montserrat' : 'font-primary'"
    >
      Create a
      <span
        v-if="page"
        :class="isContributor ? 'font-montserrat' : 'font-primary'"
        >Page</span
      >
      <span v-else :class="isContributor ? 'font-montserrat' : 'font-primary'"
        >Post</span
      >
    </h2>

    <div class="w-full flex space-x-4">
      <div v-if="!page" class="w-full lg:w-1/3">
        <InputDropdown
          :label="'Category'"
          :selected="[post_category]"
          :options="post_category_types"
          :margin-bottom="false"
          :response-selection-id="'new_post_category'"
        />
      </div>

      <div
        class="rounded relative bg-white shadow border border-truegray-300 mb-4 h-12 w-full lg:w-2/3"
      >
        <h6
          class="absolute transform duration-100 ease-linear px-1 left-3 top-1 z-40 text-truegray-700"
        >
          Title
        </h6>
        <input
          id="title"
          v-model="post_title"
          type="text"
          name="title"
          autocomplete="title"
          class="flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 text-black focus:placeholder-truegray-300"
          :class="{ 'font-primary': !isContributor }"
        />
      </div>

      <SubmitButton
        v-if="page"
        :loading-animation-options="loadingAnimationOptions"
        :is-page="page"
        :is-contributor="isContributor"
        :can-submit="canSubmit"
        :thinking="thinking"
        :submit-updates="submitUpdates"
      />
    </div>

    <SubmitButton
      v-if="!page"
      :loading-animation-options="loadingAnimationOptions"
      :is-page="page"
      :is-contributor="isContributor"
      :can-submit="canSubmit"
      :thinking="thinking"
      :submit-updates="submitUpdates"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { EventBus } from '../../js/plugins/event-bus.js'
import InputDropdown from '../components/InputDropdown.vue'
import * as loadingAnimation from '../../shared/components/loading.json'

const SubmitButton = {
  props: {
    loadingAnimationOptions: {
      type: Object,
      required: true,
    },
    isPage: {
      type: Boolean,
      required: true,
    },
    isContributor: {
      type: Boolean,
      required: true,
    },
    canSubmit: {
      type: Boolean,
      required: true,
    },
    thinking: {
      type: Boolean,
      required: true,
    },
    submitUpdates: {
      type: Function,
      required: true,
    },
  },
  template: `
    <button
      :disabled="!canSubmit || thinking"
      :class="[
        'btn relative mb-0',
        { 'h-12': isPage },
        isContributor ? 'btn-cw-cd-primary' : 'btn-primary',
        thinking ? isContributor ? 'thinking' : 'thinking-primary cursor-not-allowed' : '',
        { 'opacity-25 border border-truegray-700 cursor-not-allowed' : !canSubmit && !thinking },
      ]"
      type="submit"
      @click="submitUpdates"
    >
      <div
        class="absolute inset-0 w-full flex justify-center items-center z-20"
      >
        <lottie v-if="thinking" :options="loadingAnimationOptions" />
      </div>
      <span :class="{ 'invisible' : thinking }">Submit</span>
    </button>
  `,
}

export default {
  components: {
    InputDropdown,
    SubmitButton,
  },

  props: {
    page: { default: false },
  },

  data() {
    return {
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,

      thinking: false,
      thinkingResourceSearch: false,

      post_category_types: [
        { text: 'News', value: 1 },
        { text: 'Event', value: 3 },
        { text: 'Job', value: 2 },
      ],

      // dropdowns active
      ddCategoryActive: false,
      ddJobTypeActive: false,

      post_category_name: ['News', 'Job', 'Event'],

      canSubmit: false,

      post_category: null,
      post_category_original: null,

      post_title: '',
      post_title_original: '',

      mandatory_options: ['title', 'category'],

      update: {
        status: 0,
        category: null,
        title: null,
      },
    }
  },

  computed: {
    ...mapGetters({
      client: 'shared/getClient',
      isContributor: 'shared/isContributor',
    }),
  },

  watch: {
    post_category: {
      // Integer option
      handler: function (val) {
        this.compileUpdate('category', val)
      },

      deep: true,
    },

    post_title: {
      // String option
      handler: function (val) {
        if (this.post_title_original !== val) {
          this.compileUpdate('title', val)
        } else {
          this.compileUpdate('title', null)
        }
      },

      deep: true,
    },
  },

  mounted() {
    EventBus.$on('new_post_category', (selected) => {
      this.$set(this, 'post_category', selected)
    })

    if (this.page) {
      this.$set(this, 'post_category', 0)
    }
  },

  methods: {
    ...mapActions({
      dispatchContributorsCreatePost: 'contributors/dispatchCreatePost',
    }),

    updatePostCategory(val) {
      this.$set(this, 'post_category', val)
      this.$set(this, 'ddCategoryActive', false)
    },

    // SWISS ARMY SUBISSION DATA HELPER

    compileUpdate(node = '', val) {
      this.$set(this.update, node, val)

      let canSubmit = 0
      Object.entries(this.update).map(([, value]) => {
        canSubmit = value !== null ? (canSubmit = canSubmit + 1) : canSubmit
      })

      // Check validation = mandatory options are met | reset to zero if any are not met
      Object.entries(this.update).map(([key, value]) => {
        let required = this.mandatory_options.includes(key)
        if (value === null && required) {
          canSubmit = 0
        }
      })

      if (canSubmit > 0) {
        this.$set(this, 'canSubmit', true)
      } else {
        this.$set(this, 'canSubmit', false)
      }
    },

    resetUpdateData() {
      Object.entries(this.update).map(([key]) => {
        this.compileUpdate(key, null)
      })
    },

    // SAVE API

    async submitUpdates() {
      if (!this.canSubmit) {
        return
      }

      const handleResponse = (data) => {
        this.$set(this, 'thinking', false)

        const id = data.post_id
        if (id) {
          if (this.isContributor) {
            this.$router.push({
              name: 'ContributorsPost',
              params: { post_id: id, edit: true },
            })
          } else if (this.page) {
            this.$router.push({
              path: '/manager/pages/' + id,
              params: { post_id: id, edit: true },
            })
          } else {
            this.$router.push({
              name: 'Post',
              params: { post_id: id, edit: true },
            })
          }
        }

        if (data.settings?.website) {
          EventBus.$emit('reloadWebsiteSettings')
        }

        EventBus.$emit('showModal', false)
        EventBus.$emit('newPageComplete')

        if (data.alert && !this.page) {
          EventBus.$emit('triggerAlertResponse', data.alert)
        }

        if (data.alert && this.page) {
          EventBus.$emit('triggerAlertResponse', 'Page created')
        }
      }

      let submitData = new FormData()
      Object.entries(this.update).map(([key, value]) => {
        if (value === null) {
          //...
        } else {
          submitData.append(`${key}`, value)
        }
      })

      // Pass in the client we are dealing with --- if tenantclient 2 then we only create on the central service ...
      submitData.append('client', this.client)

      this.$set(this, 'thinking', true)

      if (this.isContributor) {
        const response = await this.dispatchContributorsCreatePost(submitData)
        if (response?.data) {
          handleResponse(response.data)
        }
      } else {
        axios
          .post('/api/auth/create-post', submitData)
          .then((response) => handleResponse(response.data))
      }
    },
  },
}
</script>
